import { createAsyncThunk } from '@reduxjs/toolkit';

import { clearCaptions, setCaptionsEnabled } from './callCaptionsSlice';
import {
  startCaptionsSessionThunk,
  stopCaptionsSessionThunk,
} from './callCaptionsSessionThunks';
import {
  selectCaptionsEnabled,
  selectRemoteInterpreterRequestsCaptions,
} from './selectors';

import type { AppDispatch, RootState } from 'features/app/store/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { selectIsConnected } from 'features/call/call-ui-state/store';
import { selectLocalCaptionIntentParticipant } from 'features/call/call-captions/helpers';
import { CallNotificationType } from 'features/call/call-events/enums';
import { sendRnsNotification } from 'features/rns/store';
import { voiceSessionIdSelector } from 'features/voice-session/store';

const SETTINGS_TOGGLE_SOURCE = 'settings-toggle';

// Tell the teammate VI that this client wants (or no longer wants) captions.
// sendRnsNotification no-ops when teaming is not active / no peer session id.
const sendCaptionIntentRnsNotification = (
  dispatch: AppDispatch,
  state: RootState,
  enabled: boolean
) => {
  const requester = selectLocalCaptionIntentParticipant(state);

  dispatch(
    sendRnsNotification({
      eventName: CallNotificationType.CAPTION_INTENT_CHANGE,
      data: {
        type: 'caption-intent',
        requester,
        enabled,
      },
    })
  );
};

const logCaptionsToggle = (
  dispatch: AppDispatch,
  message: 'Captions enabled' | 'Captions disabled',
  state: RootState
) => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'toggleCaptionsThunk',
      Message: message,
      properties:
        message === 'Captions enabled'
          ? {
              voiceSessionId: voiceSessionIdSelector(state),
              isCallConnected: selectIsConnected(state),
            }
          : { voiceSessionId: voiceSessionIdSelector(state) },
    })
  );
};

const disableCaptionsFromToggle = async (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const state = getState();
  logCaptionsToggle(dispatch, 'Captions disabled', state);
  dispatch(setCaptionsEnabled(false));
  sendCaptionIntentRnsNotification(dispatch, state, false);

  // Local display is off, but VI1 may still need lines in Redux to WSBridge-
  // forward to deaf while the remote VI's request remains on. UI stays gated
  // by captionsEnabled; only clear when no peer still needs the buffer.
  if (!selectRemoteInterpreterRequestsCaptions(getState())) {
    dispatch(clearCaptions());
  }

  try {
    // stopCaptionsSessionThunk no-ops if remoteInterpreter (or another
    // requester) is still true — shared session stays up for them.
    await dispatch(
      stopCaptionsSessionThunk({ source: SETTINGS_TOGGLE_SOURCE })
    ).unwrap();
  } catch {
    // Keep captions disabled; ingest still follows selectShouldIngestCaptions.
  }
};

const enableCaptionsFromToggle = async (
  dispatch: AppDispatch,
  state: RootState
) => {
  logCaptionsToggle(dispatch, 'Captions enabled', state);
  dispatch(setCaptionsEnabled(true));
  sendCaptionIntentRnsNotification(dispatch, state, true);

  // Start lives here — not in middleware on setCaptionsEnabled — so a failed
  // start API can revert the toggle without racing a second start.
  try {
    await dispatch(
      startCaptionsSessionThunk({ source: SETTINGS_TOGGLE_SOURCE })
    ).unwrap();
  } catch {
    // Revert local preference and notify the peer so they do not keep a stale
    // remoteInterpreter request after a failed start.
    dispatch(setCaptionsEnabled(false));
    sendCaptionIntentRnsNotification(dispatch, state, false);
  }
};

export const toggleCaptionsThunk = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>('callCaptions/toggle', async (_void, { dispatch, getState }) => {
  const state = getState();

  if (selectCaptionsEnabled(state)) {
    await disableCaptionsFromToggle(dispatch, getState);
    return;
  }

  await enableCaptionsFromToggle(dispatch, state);
});
